// FILE: src/components/FoundingTesterWelcome.js
//
// Founding Tester welcome celebration — a transient modal shown once, the first
// time a learner earns the `founding_tester` achievement (granted at
// registration; see LearnerAuthContext.ensureProfile + achievementService).
//
// It mirrors the BaselineConfidence pattern: a service/context dispatches the
// 'pm-pixel:achievement-earned' CustomEvent, HeaderUserMenu listens and opens
// this modal, and the modal is pure presentation (portal overlay, no service
// calls). Tone is warm and appreciative — this is a thank-you, not a sales pitch.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './FoundingTesterWelcome.module.css';

const BADGE_SRC = '/themes/pm-pixel/achievements/badge-founding-tester.png';
const DISCORD_URL =
  process.env.REACT_APP_DISCORD_INVITE_URL || 'https://discord.gg/QDhzGYkxub';

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {function} [props.onExplore]   — () => void (e.g. navigate to dashboard/home)
 * @param {string|null} [props.displayName]
 */
export default function FoundingTesterWelcome({ isOpen, onClose, onExplore, displayName }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const greeting = displayName ? `Welcome aboard, ${displayName}!` : 'Welcome aboard!';

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="founding-tester-welcome"
    >
      <div className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="ft-welcome-title">
        <button type="button" className={styles.closeBtn} aria-label="Close" onClick={() => onClose?.()}>×</button>

        <p className={styles.eyebrow}>★ Achievement earned</p>

        <div className={styles.badgeWrap}>
          <img className={styles.badge} src={BADGE_SRC} alt="Founding Tester badge" />
        </div>

        <h2 id="ft-welcome-title" className={styles.title}>{greeting}</h2>
        <p className={styles.subtitle}>
          You just earned the <strong>Founding Tester</strong> badge — a permanent mark of
          appreciation for helping shape PM Pixel during the beta. It now lives in your
          Milestones.
        </p>
        <p className={styles.note}>
          Your feedback genuinely steers what we build next. Thank you for being here early.
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => { onExplore?.(); onClose?.(); }}
            data-testid="ft-welcome-explore"
          >
            Start exploring
          </button>
          <a
            className={styles.secondaryBtn}
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onClose?.()}
          >
            💬 Join the Discord
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}
